import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { DEFAULT_FORM } from './constants';

export const useStaffInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await api.get('/invitations', { params });
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to load invitations', error);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const resetForm = () => setFormData(DEFAULT_FORM);

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.name) {
      toast.error('Email and name are required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/invitations', formData);
      toast.success('Invitation created successfully');
      setCreateOpen(false);
      resetForm();
      loadInvitations();
      setSelectedInvitation(response.data);
      setLinkOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (invitationId) => {
    try {
      await api.delete(`/invitations/${invitationId}`);
      toast.success('Invitation revoked');
      loadInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invitation');
    }
  };

  const handleResend = async (invitation) => {
    try {
      const response = await api.post(`/invitations/${invitation.id}/resend`);
      toast.success('Invitation resent with new code');
      setSelectedInvitation(response.data);
      setLinkOpen(true);
      loadInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/accept-invite/${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied to clipboard');
  };

  return {
    invitations,
    loading,
    statusFilter,
    setStatusFilter,
    createOpen,
    setCreateOpen,
    linkOpen,
    setLinkOpen,
    selectedInvitation,
    formData,
    setFormData,
    submitting,
    openCreate,
    handleCreate,
    handleRevoke,
    handleResend,
    copyInviteLink,
  };
};
